/**
 * KWC · Fade images in as they finish loading.
 *
 * WHY THIS IS SHAPED THE WAY IT IS
 * The obvious implementation — set every image to `opacity: 0` in CSS and clear
 * it on load — is unsafe on a live store: if this script fails, is blocked, or
 * an image never fires `load`, the page renders with invisible product photos.
 *
 * So the default state is VISIBLE. This script only marks images that are
 * genuinely still pending, and clears that mark the moment they land. If the
 * script never runs, every image simply shows immediately with no fade, which
 * is exactly today's behaviour. There is no failure mode that hides content.
 *
 * No layout shift: only `opacity` is animated, never size or position, and the
 * images keep their width/height attributes so their boxes are reserved before
 * they paint.
 */

const PENDING = 'kwc-img-pending';

/** Images already handled, so re-running is cheap and idempotent. */
const seen = new WeakSet();

/**
 * @param {HTMLImageElement} img
 */
function track(img) {
  if (seen.has(img)) return;
  seen.add(img);

  // Already decoded (cached, or eager and finished): leave it alone entirely.
  if (img.complete && img.naturalWidth > 0) return;

  img.classList.add(PENDING);

  const clear = () => img.classList.remove(PENDING);

  img.addEventListener('load', clear, { once: true });
  // On error the browser shows its own broken-image state; never leave it faded.
  img.addEventListener('error', clear, { once: true });
}

function scan(root = document) {
  if (!(root instanceof Element) && !(root instanceof Document)) return;
  for (const img of root.querySelectorAll('img')) track(/** @type {HTMLImageElement} */ (img));
}

// Respect the OS setting: no fade at all when reduced motion is requested.
if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  scan();

  // Product cards, cart drawer contents and search results are all morphed in
  // after the fact, so newly added images are picked up too. Attribute changes
  // are ignored — only added nodes matter — to keep this cheap.
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof HTMLImageElement) track(node);
        else if (node instanceof Element) scan(node);
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
}
